import { HelperActionRows } from '@/components/aiTools/HelperActionRows';
/**
 * MedicationList: the Medication List builder (med_list tool).
 *
 * A CM pastes a rough list or takes a photo of one; the model reads it into
 * structured rows, the FE buckets them into the company template's five
 * time-of-day sections, A to Z within each, and the CM reviews before
 * saving or downloading the branded .docx. (The record path is the "Make
 * med list" action on a client record's Medications card: MedListDialog.)
 *
 * FROM A PHOTO: the door shows while the tenant's photo tool is on (the
 * same usePhotoIntakeDoor predicate as the record's photo doors). A photo
 * with no medications leaves the list as it was and says so. The photo
 * reader never writes the record.
 *
 * FROM A PDF: a typed list, a pharmacy printout or a scanned page, read by
 * the same reader as a paste (the PDF goes as a document block). The
 * browser refuses a file over MED_LIST_PDF_MAX_BYTES, or one that is not a
 * PDF, before sending it; the server checks again, and refuses more than
 * 20 pages. The PDF is never stored.
 *
 * ONE ROW PER MEDICINE: every way in (a paste, a photo, a linked client's
 * record) makes rows (MedListItem), pre-ticked by the medication list's
 * rule, and the care manager edits the rows; the printed blocks are built
 * from them. With rows already on the list, a way in asks whether to
 * replace the list or add to it; with none, it fills the list. The header
 * (client, date, allergies) is kept either way. Every update is
 * functional, so an edit made while a read runs survives it.
 *
 * The list can be LINKED to a client record, or left as free text for the
 * period before every client is in Olive:
 *   - Unlinked: Client and Allergies are typed.
 *   - Linked: the client's name and DOB pre-fill from the record and the
 *     record's allergies line is pulled in read-only (the record is the
 *     source). Linking works before or after the list is built, and on a
 *     draft that was saved as free text earlier.
 *
 * DRAFTS: Save writes the reviewed list (plus the pasted source text and the
 * link) to the ai_drafts inbox; `?draft=<id>` reopens it here with the same
 * state, A to Z in each block, and the next Save updates that draft in place.
 * Every Save carries the model call that produced the current rows, so a
 * resumed draft rebuilt from a new paste or a photo relinks to that call.
 * The date is a real date input pre-filled with the practice's today and
 * capped there.
 *
 * Mirrors BrandFormatter: capability gate -> ToolPageShell with the paste form
 * as input and the reviewed list + Save / Download row as output. Download is
 * a stateless POST of the reviewed document, so it is available as soon as a
 * list is built, saved or not.
 */

import { type ChangeEvent, type ReactNode, useEffect, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Camera, FileText, Sparkles } from 'lucide-react';
import { useTenant } from '@/lib/auth';
import { canAuthorClinicalNotes, canReadClinicalRecord, canUseAiTools, canReadEveryClient, canViewAnyClientRoster } from '@/lib/capabilities';
import { useOwnCaseloadPicks } from '@/lib/cmHomeApi';
import { Button } from '@/components/primitives/Button';
import { DownloadDocxButton } from '@/components/DownloadDocxButton';
import { DocumentPrintPdfButton } from '@/components/DocumentPrintPdfButton';
import { ToolPageShell, ToolDraftingState } from '@/components/aiTools/ToolPageShell';
import { EmptyAiToolsTile } from '@/components/aiTools/EmptyAiToolsTile';
import { MedListReview, type MedListLinkedClient } from '@/components/aiTools/MedListReview';
import { MedListRecordUpdate } from '@/components/aiTools/MedListRecordUpdate';
import { ClientLookupField } from '@/components/aiTools/ClientLookupField';
import { ErrorNotice } from '@/components/ErrorNotice';
import { TextTooLongNote, isTextTooLong } from '@/components/aiTools/TextTooLongNote';
import { useTenantConfig } from '@/hooks/useTenantConfig';
import { readAiInputMaxChars } from '@/lib/aiInputLimits';
import { aiToolFailureCopy, aiToolFailureOf, type AiToolFailure } from '@/lib/aiTextTooLong';
import { useInvalidateOnCeilingRefusal } from '@/hooks/useInvalidateOnCeilingRefusal';
import { ReadingIndicator } from '@/components/notes/ReadingIndicator';
import { aiGenerateKeyOf, aiSaveKeyOf, medListPdfKeyOf, photoIntakeKeyOf, type KeyedError } from '@/lib/errorCopy';
import { usePhotoIntakeDoor } from '@/lib/aiToolToggles';
import { downscaleImageForIntake, extractPhotoIntake } from '@/lib/photoIntakeApi';
import { aiToolsApi, ApiError, type ApiError as ApiErrorType } from '@/lib/api';
import { useAiDraft } from '@/lib/aiDrafts';
import { useClientRecord } from '@/lib/clientRecordApi';
import { practiceTodayIso } from '@/lib/practiceDate';
import { proseDate } from '@/lib/proseDate';
import {
  MED_LIST_PDF_MAX_BYTES,
  activeMedicationsForList,
  applyLinkedClient,
  bucketsToRows,
  emptyMedListDoc,
  isMedListDateValid,
  itemFromInput,
  itemFromPhoto,
  itemFromRecord,
  itemsOf,
  medListChangesFromRecord,
  sortMedListBuckets,
  withItems,
  type MedListChange,
  type MedListDoc,
  type MedListItem,
} from '@/lib/medList';
import { AI_TOOLS } from './AiTools';
import { KeptWorkNotice } from '@/components/KeptWorkNotice';
import { keptWorkKey, useKeptWork } from '@/lib/keptWork';

const TOOL = AI_TOOLS.find((t) => t.slug === 'med-list')!;

const INPUT_CLS =
  'mt-1.5 block w-full min-h-11 rounded-[12px] border border-sage-400/20 bg-paper px-3.5 py-2.5 text-sm text-ink placeholder:text-sage-300 outline-none transition-[border-color,box-shadow] duration-[var(--dur-base)] ease-quiet focus:border-sage-400 focus:ring-[3px] focus:ring-sage-800/[6%]';
const LABEL_CLS = 'block text-[0.8125rem] font-semibold text-sage-800';

/** A Save's input, read from the page when the button is pressed. */
interface SaveInput {
  doc: MedListDoc;
  draftId: string | null;
  clientId: string | null;
  sourceText: string;
  invocationId: number | null;
}

/** The browser's own refusal of a picked file, before anything is sent:
 *  not a PDF by its type or name, or larger than the door takes. */
function pdfRefusalHere(file: File): 'pdf_not_a_pdf' | 'pdf_too_large' | null {
  const looksPdf = file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
  if (!looksPdf) return 'pdf_not_a_pdf';
  if (file.size > MED_LIST_PDF_MAX_BYTES) return 'pdf_too_large';
  return null;
}

/** A picked PDF as base64, or null when the file could not be read. */
async function pdfAsBase64(file: File): Promise<string | null> {
  try {
    const dataUrl = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = () => reject(reader.error);
      reader.readAsDataURL(file);
    });
    return dataUrl.slice(dataUrl.indexOf(',') + 1);
  } catch {
    return null;
  }
}

/** A photo read's input, read from the page when the photo is picked. */
interface PhotoReadInput {
  file: File;
  clientId: string | null;
}

/** What an expired sign-in keeps of this page (src/lib/keptWork.ts). */
interface KeptMedList {
  doc: MedListDoc | null;
  sourceText: string;
  invocationId: number | null;
  clientId: string | null;
  draftId: string | null;
}

export function MedicationList(): ReactNode {
  const { whoami, tenantSlug } = useTenant();
  const queryClient = useQueryClient();
  const [searchParams] = useSearchParams();
  const today = practiceTodayIso();

  const [sourceText, setSourceText] = useState('');
  // The list's input ceiling, from the tenant config; the factory value
  // until the config has loaded.
  const tenantConfig = useTenantConfig(tenantSlug).data?.config;
  const maxChars = readAiInputMaxChars(tenantConfig, 'med_list');
  const [doc, setDoc] = useState<MedListDoc | null>(null);
  const [invocationId, setInvocationId] = useState<number | null>(null);
  const [clientId, setClientId] = useState<string | null>(null);
  const [draftId, setDraftId] = useState<string | null>(null);
  // The pasted list's last failure: a registry key, or a refusal for
  // length with the box it names.
  const [failure, setFailure] = useState<AiToolFailure | null>(null);
  const onCeilingRefusal = useInvalidateOnCeilingRefusal(tenantSlug);
  const [saveError, setSaveError] = useState<KeyedError | null>(null);
  const [saved, setSaved] = useState(false);
  const [photoErrorKey, setPhotoErrorKey] = useState<string | null>(null);
  const [photoEmpty, setPhotoEmpty] = useState(false);
  // Rows a way in brought while the list already had rows: they wait for
  // the care manager's Replace the list or Add to the list.
  const [pendingItems, setPendingItems] = useState<MedListItem[] | null>(null);
  // The record's changes still to come that travel with pending rows taken
  // from the record (LP-41); null when the pending rows came from a read.
  const [pendingChanges, setPendingChanges] = useState<MedListChange[] | null>(null);
  const photoFileRef = useRef<HTMLInputElement>(null);
  const pdfFileRef = useRef<HTMLInputElement>(null);
  const [pdfErrorKey, setPdfErrorKey] = useState<string | null>(null);
  const [pdfEmpty, setPdfEmpty] = useState(false);

  const capEligible = canUseAiTools(whoami);
  // The "From a photo" door: one predicate with the record's photo doors.
  const photoDoor = usePhotoIntakeDoor(tenantSlug, capEligible);
  // The picker searches every client the viewer may open (GET /v1/clients
  // answers with every client or the viewer's lane), own clients first, and
  // needs the record it will read from (read_clinical_record); without
  // either, free text.
  const canReadRecord = canReadClinicalRecord(whoami);
  const canLookup = canViewAnyClientRoster(whoami) && canReadRecord;
  const ownCaseload = useOwnCaseloadPicks(tenantSlug, canLookup && canAuthorClinicalNotes(whoami) && !canReadEveryClient(whoami));

  // Resume: ?draft=<id> loads the saved list once, then this page owns the
  // state (a background refetch never clobbers in-progress edits).
  const resumeId = searchParams.get('draft');
  const { draft, isLoading: draftLoading, isError: draftError, error: draftErr } = useAiDraft(
    tenantSlug,
    resumeId ?? '',
    { enabled: capEligible && resumeId !== null },
  );
  const hydratedRef = useRef<string | null>(null);
  useEffect(() => {
    if (!draft || draft.tool_name !== 'med_list' || hydratedRef.current === draft.id) return;
    hydratedRef.current = draft.id;
    const document = draft.input_payload.document;
    setDraftId(draft.id);
    setInvocationId(draft.ai_invocation_id);
    setClientId(draft.client_id);
    setSourceText(draft.input_payload.source_text ?? '');
    if (!document) {
      setDoc(emptyMedListDoc(today));
      return;
    }
    // A draft saved with rows reopens as those rows; one saved as blocks
    // before rows existed reopens as the rows its blocks came from.
    const base: MedListDoc = {
      ...document,
      client_last_name: document.client_last_name ?? null,
      client_first_name: document.client_first_name ?? null,
      date: document.date ?? today,
      buckets: sortMedListBuckets(document.buckets),
    };
    setDoc(withItems(base, document.items ?? bucketsToRows(base.buckets)));
  }, [draft, today]);

  // The linked client's record: the source for name, DOB, and allergies.
  const { record } = useClientRecord(tenantSlug, clientId ?? '', {
    enabled: canReadRecord && clientId !== null,
  });
  const linkedRecord = clientId !== null && record !== null && record.clientId === clientId ? record : null;
  // Whenever the linked record is in hand and a list exists, the header
  // follows the record (linking before or after the build both land here).
  useEffect(() => {
    if (linkedRecord === null) return;
    setDoc((current) => (current === null ? current : applyLinkedClient(current, linkedRecord)));
  }, [linkedRecord]);

  const linkedClient: MedListLinkedClient | null =
    clientId !== null
      ? {
          clientId,
          displayName: linkedRecord?.vitals.displayName ?? '',
          dob: linkedRecord?.vitals.dob ?? null,
          recordHref: `/t/${tenantSlug}/clients/${clientId}`,
        }
      : null;

  // A build replaces the rows and keeps the header. Deterministic bucketing
  // runs here (the record path's rules). A linked record fills the header;
  // otherwise the fields start blank for the CM to type, and the date is
  // today. Functional, so an edit made while the build ran survives it.
  /** Put rows on the list: the list's own rows are replaced, or these are
   *  added after them; the header stays. The changes still to come travel
   *  with the rows they belong to (LP-41): rows from the record bring the
   *  record's (`changes`), rows from a read replace them with none, and
   *  added rows from a read keep the list's. */
  const putItems = (incoming: MedListItem[], how: 'replace' | 'add', changes: MedListChange[] | null): void => {
    setDoc((current) => {
      const base = current ?? emptyMedListDoc(today);
      const items = how === 'add' ? [...itemsOf(base), ...incoming] : incoming;
      const changes_coming = changes ?? (how === 'add' ? (base.changes_coming ?? []) : []);
      const built = { ...withItems(base, items), changes_coming };
      return linkedRecord ? applyLinkedClient(built, linkedRecord) : built;
    });
    setPendingItems(null);
    setPendingChanges(null);
    setSaved(false);
  };
  /** A way in's rows: straight onto an empty list, else held for the care
   *  manager's Replace or Add. `changes` as putItems. */
  const offerItems = (incoming: MedListItem[], changes: MedListChange[] | null = null): void => {
    if (doc === null || itemsOf(doc).length === 0) {
      putItems(incoming, 'replace', changes);
      return;
    }
    setPendingItems(incoming);
    setPendingChanges(changes);
  };

  // A read's rows are handed to `incoming`; the effect below offers them
  // against the list as it is when they land (not as it was at the press,
  // nor an effect late), so a mutation callback reads nothing from a render.
  const [incoming, setIncoming] = useState<MedListItem[] | null>(null);
  useEffect(() => {
    if (incoming === null) return;
    setIncoming(null);
    offerItems(incoming);
    // Keyed on the arrival only: offerItems reads this render's list, and
    // the effect runs once per batch of rows.
  }, [incoming]);

  // The source text is read at the press and travels as the variables.
  const mutation = useMutation({
    mutationFn: (source: string) => aiToolsApi.draftMedList(tenantSlug, { source_text: source }),
    onMutate: () => {
      // Starting either door clears the other door's notices.
      setPhotoErrorKey(null);
      setPhotoEmpty(false);
    },
    onSuccess: (res) => {
      setFailure(null);
      setSaved(false);
      setInvocationId(res.ai_invocation_id);
      setIncoming(res.medications.map(itemFromInput));
    },
    onError: (e: ApiErrorType, source) => {
      onCeilingRefusal(e);
      // The pasted list is the only text this route takes.
      setFailure(aiToolFailureOf(e, 'med_list_generate', 'source_text', { source_text: source }, (x) => aiGenerateKeyOf(x) ?? 'unknown'));
    },
  });

  // The photo read: the medication rows only (the providers on a photo are
  // not part of this list), nothing written to the record.
  const photoMutation = useMutation({
    mutationFn: async ({ file, clientId: linkedClientId }: PhotoReadInput) => {
      const image = await downscaleImageForIntake(file);
      return extractPhotoIntake(tenantSlug, { image, clientId: linkedClientId ?? undefined });
    },
    onMutate: () => {
      setFailure(null);
      setPhotoErrorKey(null);
      setPhotoEmpty(false);
    },
    onSuccess: (res) => {
      // A photo with no medications leaves the list and its link alone.
      if (res.medications.length === 0) {
        setPhotoEmpty(true);
        return;
      }
      setSaved(false);
      setInvocationId(res.ai_invocation_id);
      setIncoming(res.medications.map(itemFromPhoto));
    },
    onError: (e: unknown) => {
      setPhotoErrorKey(photoIntakeKeyOf(e));
    },
  });

  const pdfMutation = useMutation({
    mutationFn: async (file: File) => {
      const data = await pdfAsBase64(file);
      if (data === null) return null;
      return aiToolsApi.draftMedList(tenantSlug, { source_pdf: { data_base64: data, file_name: file.name } });
    },
    onMutate: () => {
      setFailure(null);
      setPhotoErrorKey(null);
      setPhotoEmpty(false);
      setPdfErrorKey(null);
      setPdfEmpty(false);
    },
    onSuccess: (res) => {
      if (res === null) {
        setPdfErrorKey('invalid_input');
        return;
      }
      if (res.medications.length === 0) {
        setPdfEmpty(true);
        return;
      }
      setInvocationId(res.ai_invocation_id);
      setIncoming(res.medications.map(itemFromInput));
    },
    onError: (e: unknown) => {
      setPdfErrorKey(medListPdfKeyOf(e));
    },
  });

  const saveMutation = useMutation({
    mutationFn: async (input: SaveInput): Promise<string> => {
      const body = {
        client_id: input.clientId,
        source_text: input.sourceText.trim().length > 0 ? input.sourceText.trim() : null,
        document: input.doc,
        // The call that produced the current rows; a PUT relinks the draft to it.
        ai_invocation_id: input.invocationId,
      };
      if (input.draftId !== null) {
        const res = await aiToolsApi.updateMedListDraft(tenantSlug, input.draftId, body);
        return res.draft.id;
      }
      const res = await aiToolsApi.saveMedListDraft(tenantSlug, body);
      return res.id;
    },
    onSuccess: (id) => {
      setDraftId(id);
      setSaveError(null);
      setSaved(true);
      void queryClient.invalidateQueries({ queryKey: ['ai-tools', 'drafts', tenantSlug] });
    },
    onError: (e: unknown) => {
      setSaved(false);
      if (e instanceof ApiError && e.status === 404) {
        const body = e.body as { error?: string } | null;
        if (body?.error === 'client_not_found') {
          setSaveError({ key: 'invalid_payload' });
          return;
        }
      }
      setSaveError(aiSaveKeyOf(e));
    },
  });

  // An expired sign-in keeps the list as edited and what it was built from
  // (src/lib/keptWork.ts); before a build, the pasted text.
  const kept = useKeptWork<KeptMedList>(
    keptWorkKey(tenantSlug, 'med-list', resumeId),
    () => {
      if (saved) return null;
      if (doc === null) return sourceText.trim() === '' ? null : { doc: null, sourceText, invocationId, clientId, draftId };
      return { doc, sourceText, invocationId, clientId, draftId };
    },
    (k) => {
      // The kept copy is newer than the saved row a resume would load.
      hydratedRef.current = resumeId;
      setDoc(k.doc);
      setSourceText(k.sourceText);
      setInvocationId(k.invocationId);
      setClientId(k.clientId);
      setDraftId(k.draftId);
      setSaved(false);
    },
  );
  // Saved: the kept copy has done its job.
  useEffect(() => {
    if (saved) kept.clear();
  }, [saved]);

  if (!capEligible) {
    return (
      <main className="max-w-6xl mx-auto px-4 py-6">
        <EmptyAiToolsTile />
      </main>
    );
  }

  const linkClient = (id: string): void => {
    setClientId(id);
    setSaved(false);
  };
  const unlinkClient = (): void => {
    setClientId(null);
    setSaved(false);
  };

  const dateValid = doc !== null && isMedListDateValid(doc.date, today);
  const sourceTooLong = isTextTooLong(sourceText, maxChars);
  const canSubmit = sourceText.trim().length > 0 && !sourceTooLong;
  const failureCopy =
    failure === null ? null : aiToolFailureCopy(failure, (entry) => readAiInputMaxChars(tenantConfig, entry));
  const busy = mutation.isPending || saveMutation.isPending || photoMutation.isPending || pdfMutation.isPending;

  const onPickPdf = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    const refusal = pdfRefusalHere(file);
    if (refusal !== null) {
      setPdfEmpty(false);
      setPdfErrorKey(refusal);
      return;
    }
    pdfMutation.mutate(file);
  };

  const onPickPhoto = (e: ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    // Reset the input so picking the same file again fires change again.
    e.target.value = '';
    if (file) photoMutation.mutate({ file, clientId });
  };

  const clientBlock = (
    <section className="space-y-2" data-testid="med-list-client-block">
      <span className={LABEL_CLS}>Client record</span>
      {linkedClient ? (
        <div
          className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-[12px] border border-sage-400/25 bg-sage-50 px-3.5 py-2.5 text-sm"
          data-testid="med-list-linked-client"
        >
          <span className="font-semibold text-sage-800">
            {linkedClient.displayName.length > 0 ? linkedClient.displayName : 'Loading the record…'}
          </span>
          <span className="text-xs text-ink-soft" data-testid="med-list-linked-dob">
            {linkedRecord === null
              ? ''
              : linkedClient.dob
                ? `Date of birth ${proseDate(linkedClient.dob)}`
                : 'Date of birth not recorded'}
          </span>
          <Link
            to={linkedClient.recordHref}
            className="text-xs font-semibold text-sage-800 underline underline-offset-2"
            data-testid="med-list-open-record"
          >
            Open record
          </Link>
          <Button variant="ghost" size="sm" onClick={unlinkClient} disabled={busy} data-testid="med-list-unlink">
            Unlink
          </Button>
          {linkedRecord !== null && (
            <Button
              variant="secondary"
              size="sm"
              disabled={busy}
              onClick={() =>
                offerItems(
                  activeMedicationsForList(linkedRecord.medications, today).map(itemFromRecord),
                  medListChangesFromRecord(linkedRecord, today),
                )
              }
              data-testid="med-list-fill-record"
            >
              Use the record&rsquo;s medications
            </Button>
          )}
        </div>
      ) : (
        <>
          <ClientLookupField
            tenantSlug={tenantSlug}
            canLookup={canLookup}
            caseload={ownCaseload}
            onPick={(c) => linkClient(c.id)}
            disabled={busy}
            inputId="ml-client-lookup"
            testIdPrefix="med-list-client"
            label="Link to a client"
            hint="Pulls the client's name, date of birth, and allergies from their record. Leave it unlinked to type them for now."
            className="relative"
          />
          {!canLookup && (
            <p className="text-xs text-ink-soft" data-testid="med-list-free-text-note">
              Type the client&rsquo;s name and allergies on the list below.
            </p>
          )}
        </>
      )}
    </section>
  );

  const inputForm = (
    <>
    {kept.restored && <KeptWorkNotice onDiscard={kept.discard} />}
    <form
      onSubmit={(e) => {
        e.preventDefault();
        mutation.mutate(sourceText.trim());
      }}
      className="space-y-4"
      data-testid="med-list-form"
    >
      {clientBlock}
      <div>
        <label htmlFor="ml-source" className={LABEL_CLS}>
          Paste a medication list
        </label>
        <p className="text-xs text-ink-soft mt-0.5">
          Paste the names, doses, and timing. It gets organized into the company medication list
          template. Nothing is invented, and you review every row before saving or downloading.
        </p>
        <textarea
          id="ml-source"
          value={sourceText}
          onChange={(e) => setSourceText(e.target.value)}
          rows={14}
          disabled={mutation.isPending}
          placeholder="e.g. Lisinopril 10 mg by mouth every morning for blood pressure&#10;Metformin 500 mg twice a day for diabetes&#10;Acetaminophen 500 mg as needed for pain"
          className={INPUT_CLS}
          data-testid="med-list-source"
        />
        {sourceTooLong && (
          <TextTooLongNote
            surface="med_list_generate"
            maxChars={maxChars}
            kind="split"
            text={sourceText}
            data-testid="med-list-source-too-long"
          />
        )}
      </div>

      {failureCopy !== null && (
        <ErrorNotice
          surface="med_list_generate"
          code={failureCopy.key}
          vars={failureCopy.vars}
          data-testid="med-list-error"
        />
      )}
      {photoErrorKey !== null && (
        <ErrorNotice surface="med_list_photo_generate" code={photoErrorKey} data-testid="med-list-photo-error" />
      )}
      {pdfErrorKey !== null && (
        <ErrorNotice surface="med_list_pdf_generate" code={pdfErrorKey} data-testid="med-list-pdf-error" />
      )}
      {pdfEmpty && (
        <p
          role="status"
          className="rounded-[12px] border border-sage-800/[8%] bg-paper px-3 py-2 text-sm text-ink-soft"
          data-testid="med-list-pdf-empty"
        >
          Try a photo of the list, or paste it.
        </p>
      )}
      {photoEmpty && (
        <p
          role="status"
          className="rounded-[12px] border border-sage-800/[8%] bg-paper px-3 py-2 text-sm text-ink-soft"
          data-testid="med-list-photo-empty"
        >
          Try a clearer photo, or paste the list.
        </p>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button
          type="submit"
          variant={doc !== null ? 'ghost' : 'primary'}
          size={doc !== null ? 'sm' : 'md'}
          disabled={!canSubmit || busy}
          loading={mutation.isPending}
          icon={<Sparkles size={17} aria-hidden="true" />}
          data-testid="med-list-build"
        >
          {mutation.isPending ? 'Organizing the list…' : doc !== null ? 'Build the list again' : 'Build medication list'}
        </Button>
        <Button
          variant={doc !== null ? 'ghost' : 'secondary'}
          size={doc !== null ? 'sm' : 'md'}
          disabled={busy}
          loading={pdfMutation.isPending}
          icon={<FileText size={17} aria-hidden="true" />}
          onClick={() => pdfFileRef.current?.click()}
          data-testid="med-list-from-pdf"
        >
          {pdfMutation.isPending ? 'Reading the PDF…' : 'From a PDF'}
        </Button>
        <input
          ref={pdfFileRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={onPickPdf}
          data-testid="med-list-pdf-file"
        />
        {photoDoor && (
          <>
            <Button
              variant={doc !== null ? 'ghost' : 'secondary'}
              size={doc !== null ? 'sm' : 'md'}
              disabled={busy}
              loading={photoMutation.isPending}
              icon={<Camera size={17} aria-hidden="true" />}
              onClick={() => photoFileRef.current?.click()}
              data-testid="med-list-from-photo"
            >
              {photoMutation.isPending ? 'Reading the photo…' : 'From a photo'}
            </Button>
            {/* Hidden file input; no `capture`, so a phone offers the camera
                and the photo library both (LP-27). */}
            <input
              ref={photoFileRef}
              type="file"
              accept="image/*"
              className="hidden"
              onChange={onPickPhoto}
              data-testid="med-list-photo-file"
            />
          </>
        )}
      </div>
    </form>
    </>
  );

  const draftMissing = resumeId !== null && draftError && draftErr instanceof ApiError && draftErr.status === 404;

  const outputContent: ReactNode | undefined = draftMissing ? (
    <p
      className="text-sm text-ink-soft bg-sand border border-sage-800/[8%] rounded-lg px-4 py-6 text-center"
      data-testid="med-list-draft-missing"
    >
      This draft is no longer available.{' '}
      <Link to={`/t/${tenantSlug}/ai-tools/drafts`} className="underline">
        Back to drafts
      </Link>
    </p>
  ) : doc !== null ? (
    <div className="space-y-4">
      {pendingItems !== null && (
        <div
          role="group"
          aria-label="Replace the list or add to it"
          className="rounded-[12px] border border-sage-400/30 bg-sage-50 px-3.5 py-3 space-y-2"
          data-testid="med-list-merge"
        >
          <p className="text-sm text-ink">
            {pendingItems.length === 1 ? '1 medication was read.' : `${pendingItems.length} medications were read.`} Replace
            the list with them, or add them to it?
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="secondary" size="sm" onClick={() => putItems(pendingItems, 'replace', pendingChanges)} data-testid="med-list-merge-replace">
              Replace the list
            </Button>
            <Button variant="secondary" size="sm" onClick={() => putItems(pendingItems, 'add', pendingChanges)} data-testid="med-list-merge-add">
              Add to the list
            </Button>
            <Button variant="ghost" size="sm" onClick={() => {
                setPendingItems(null);
                setPendingChanges(null);
              }} data-testid="med-list-merge-cancel">
              Keep the list as it is
            </Button>
          </div>
        </div>
      )}
      <MedListReview
        doc={doc}
        onChange={(next) => {
          setDoc(next);
          setSaved(false);
        }}
        today={today}
        linkedClient={linkedClient}
        careTeam={linkedRecord ? { providers: linkedRecord.providers, pronouns: linkedRecord.vitals.pronouns ?? null } : null}
        emptyLabel="Add the medications to the text and build again."
      />
      <HelperActionRows
        testId="med-list-download-row"
        primary={
          <>
            <DownloadDocxButton
              url={aiToolsApi.medListDownloadUrl(tenantSlug)}
              postBody={doc}
              filename="Medication List.docx"
              variant="primary"
              testId="med-list-download"
            >
              Download .docx
            </DownloadDocxButton>
            <DocumentPrintPdfButton
              url={aiToolsApi.medListDownloadUrl(tenantSlug)}
              postBody={doc}
              filename="Medication List.docx"
              testId="med-list-print-pdf"
            />
          </>
        }
        secondary={
          <Button
            variant="secondary"
            onClick={() =>
              saveMutation.mutate({
                doc,
                draftId,
                clientId,
                sourceText,
                invocationId,
              })
            }
            disabled={!dateValid || busy}
            loading={saveMutation.isPending}
            data-testid="med-list-save"
          >
            {saveMutation.isPending ? 'Saving…' : draftId !== null ? 'Save changes' : 'Save draft'}
          </Button>
        }
      />
      {saved && (
        <p className="text-xs text-sage-700" role="status" data-testid="med-list-saved">
          {clientId !== null
            ? "Saved. It's on the client's Documents tab for anyone who opens the client."
            : 'Saved to your drafts.'}{' '}
          <Link to={`/t/${tenantSlug}/ai-tools/drafts`} className="underline">
            View drafts
          </Link>
        </p>
      )}
      {saved && clientId !== null && linkedRecord !== null && (
        <MedListRecordUpdate clientId={clientId} items={itemsOf(doc)} record={linkedRecord.medications} />
      )}
      {saveError !== null && (
        <ErrorNotice
          surface="med_list_draft_save"
          code={saveError.key}
          vars={saveError.vars}
          data-testid="med-list-save-error"
        />
      )}
    </div>
  ) : photoMutation.isPending ? (
    <ReadingIndicator kind="photo" testId="med-list-photo-reading" />
  ) : pdfMutation.isPending ? (
    <ReadingIndicator kind="pdf" testId="med-list-pdf-reading" />
  ) : mutation.isPending || (resumeId !== null && draftLoading) ? (
    <ToolDraftingState />
  ) : undefined;

  return (
    <ToolPageShell
      tool={TOOL}
      tenantSlug={tenantSlug}
      inputTitle="Your medication list"
      outputTitle="Medication list"
      inputPlaceholder={inputForm}
      outputContent={outputContent}
    />
  );
}
